import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { DropResult } from "@hello-pangea/dnd";
import { apiPatch, apiPost } from "../../../services/Api/apiCall";
import { queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { ExecutionStep } from "./ExecutionStepFormShared";
import { ExecutionJob } from "./caseExecutionJobDetailsTypes";
import { isExecutionStepReorderableStatus, reorderExecutionStepsLocally } from "./caseExecutionJobDetailsUtils";

type UseExecutionJobActionsParams = {
  executionJob?: ExecutionJob;
  caseIri?: string;
  isExecutionAllowed: boolean;
  isRejectionAllowed: boolean;
};

type ExecuteExecutionJobParams = {
  executionJobId: number;
  caseIri?: string | null;
  enqueueSnackbar: ReturnType<typeof useSnackbar>["enqueueSnackbar"];
  queryClient: ReturnType<typeof useQueryClient>;
};

export const executeExecutionJob = async ({
  executionJobId,
  caseIri,
  enqueueSnackbar,
  queryClient,
}: ExecuteExecutionJobParams) => {
  const refreshExecutionJob = async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.item("execution_jobs", executionJobId)
    });
  };

  const refreshExecutionJobList = async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("execution_jobs", { case: caseIri })
    });
  };

  try {
    await apiPost(`/execution_jobs/${executionJobId}/execute`, {});
    enqueueSnackbar("Job-Ausführung wurde gestartet.", {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
    await refreshExecutionJob();
    await refreshExecutionJobList();
  } catch (e: any) {
    enqueueSnackbar(e?.errors?.message || "Job konnte nicht ausgeführt werden.", {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
    throw e;
  }
};

type RejectExecutionJobParams = {
  executionJobId: number;
  caseIri?: string | null;
  enqueueSnackbar: ReturnType<typeof useSnackbar>["enqueueSnackbar"];
  queryClient: ReturnType<typeof useQueryClient>;
};

export const rejectExecutionJob = async ({
  executionJobId,
  caseIri,
  enqueueSnackbar,
  queryClient,
}: RejectExecutionJobParams) => {
  const refreshExecutionJob = async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.item("execution_jobs", executionJobId)
    });
  };

  const refreshExecutionJobList = async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("execution_jobs", { case: caseIri })
    });
  };

  try {
    await apiPost(`/execution_jobs/${executionJobId}/reject`, {});
    enqueueSnackbar("Job wurde abgelehnt.", {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
    await refreshExecutionJob();
    await refreshExecutionJobList();
  } catch (e: any) {
    enqueueSnackbar(e?.errors?.message || "Job konnte nicht abgelehnt werden.", {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
    throw e;
  }
};

export const useExecutionJobActions = ({
  executionJob,
  caseIri,
  isExecutionAllowed,
  isRejectionAllowed,
}: UseExecutionJobActionsParams) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isExecuting, setIsExecuting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isReorderingSteps, setIsReorderingSteps] = useState(false);
  const [stepIdsWithPendingStatusToggle, setStepIdsWithPendingStatusToggle] = useState<number[]>([]);
  const [orderedSteps, setOrderedSteps] = useState<ExecutionStep[]>([]);

  useEffect(() => {
    setOrderedSteps(executionJob?.steps || []);
  }, [executionJob?.steps]);

  const canReorderSteps =
    !isExecuting &&
    !isRejecting &&
    !isReorderingSteps &&
    stepIdsWithPendingStatusToggle.length === 0 &&
    orderedSteps.every((step) => isExecutionStepReorderableStatus(step.status));

  const refreshExecutionJob = async () => {
    if (!executionJob) {
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: queryKeys.item("execution_jobs", executionJob.id)
    });
  };

  const refreshExecutionJobList = async () => { // selbe function 3 mal in der selben datei definiert. 10/10
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("execution_jobs", { case: caseIri })
    });
  };

  const refreshExecutionJobAndList = async () => {
    await refreshExecutionJob();
    await refreshExecutionJobList();
  };

  const executeJob = async () => {
    if (!executionJob || !isExecutionAllowed || isRejecting) {
      return;
    }

    setIsExecuting(true);

    try {
      await executeExecutionJob({
        executionJobId: executionJob.id,
        caseIri,
        enqueueSnackbar,
        queryClient,
      });
    } catch (e: any) {
      // Snackbar is handled in executeExecutionJob.
    }

    setIsExecuting(false);
  };

  const rejectJob = async () => {
    if (!executionJob || !isRejectionAllowed || isExecuting) {
      return;
    }

    setIsRejecting(true);

    try {
      await rejectExecutionJob({
        executionJobId: executionJob.id,
        caseIri,
        enqueueSnackbar,
        queryClient,
      });
    } catch (e: any) {
      // Snackbar is handled in rejectExecutionJob.
    }

    setIsRejecting(false);
  };

  const reorderExecutionSteps = async ({ destination, source }: DropResult) => {
    if (!executionJob || !destination || source.index === destination.index || !canReorderSteps) {
      return;
    }

    const previousSteps = orderedSteps;
    const reorderedSteps = reorderExecutionStepsLocally(orderedSteps, source.index, destination.index);
    setOrderedSteps(reorderedSteps);
    setIsReorderingSteps(true);

    try {
      await apiPost(`/execution_jobs/${executionJob.id}/reorder_steps`, {
        stepIds: reorderedSteps.map((step) => step.id),
      });
      await refreshExecutionJobAndList();
      enqueueSnackbar("Reihenfolge der Schritte wurde gespeichert.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } catch (e: any) {
      setOrderedSteps(previousSteps);
      enqueueSnackbar(e?.errors?.message || "Reihenfolge der Schritte konnte nicht gespeichert werden.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    setIsReorderingSteps(false);
  };

  const toggleStepSkipped = async (step: ExecutionStep) => {
    if (!executionJob || stepIdsWithPendingStatusToggle.includes(step.id)) {
      return;
    }

    setStepIdsWithPendingStatusToggle((previousStepIds) => [...previousStepIds, step.id]);

    try {
      const nextStatus = step.status === "skipped" ? "pending" : "skipped";
      await apiPatch("/execution_steps", step.id, { status: nextStatus });
      enqueueSnackbar(nextStatus === "skipped" ? "Schritt wurde übersprungen." : "Schritt wurde wieder aktiviert.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
      await refreshExecutionJobAndList();
    } catch (e: any) {
      enqueueSnackbar(e?.errors?.message || "Schritt konnte nicht aktualisiert werden.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    setStepIdsWithPendingStatusToggle((previousStepIds) => previousStepIds.filter((stepId) => stepId !== step.id));
  };

  return {
    canReorderSteps,
    executeJob,
    isExecuting,
    isRejecting,
    isReorderingSteps,
    orderedSteps,
    refreshExecutionJob,
    rejectJob,
    reorderExecutionSteps,
    stepIdsWithPendingStatusToggle,
    toggleStepSkipped,
  };
};
